import { ILinkedServiceTypeMetadata } from 'app/extensibility/metadatas/resource-type/linked-service-type-metadata';
import { EncryptionMethod, encryptionMethodOptions } from 'app/registries/consts/postgre-consts';
import { format, getConnectorLegacyNameByFlag } from 'app/shared/utils/string.util';
import { adfModelTypeMap } from '../consts/adf-model-type-map-const';
import { ConnectionAuthKind } from 'shell-models-lib';
import { BuiltInDataSourceOptionId, DataSourceOptionKind } from '@powerquery/mashup-editor-client';
import { EnvironmentInjector } from '@angular/core';
import { GlobalVariables } from 'app/services/globals';

export const postgreSqlMetadata = async (injector: EnvironmentInjector) => {
    const result: ILinkedServiceTypeMetadata = {
        type: adfModelTypeMap.postgreSql.linkedServiceType,
        disabled: __IS_FABRIC_DPE__,
        notShowInTypeSelection: !GlobalVariables.showLegacy,
        isDeprecated: !GlobalVariables.showLegacy,
        displayName: getConnectorLegacyNameByFlag(stringResources.PostgreSql_DisplayText, true),
        warningMessage: format(
            stringResources.Connector_Legacy_IsNotSupportedWarning_alt2,
            stringResources.LinkedServiceLowerCase,
            stringResources.PostgreSql_DisplayText
        ),
        warningMessageHyperlink:
            'https://learn.microsoft.com/azure/data-factory/connector-postgresql?tabs=data-factory', // Need to update
        linkedServiceKind: 'table',
        supportedInTrident: true,
        pqoDataSourceOption: {
            kind: DataSourceOptionKind.BuiltIn,
            id: BuiltInDataSourceOptionId.PostgreSql
        },
        supportedAuthKindsInTrident: [ConnectionAuthKind.Basic],
        icon: {
            className: 'svg-i-100x100-PostgreSQL'
        },
        isFirstClass: true,
        fetchTableNameFromItemPath: true,
        supportDatasetV2: true,
        supportFirstClassParameterExperience: true,
        tableColumnNameQuotes: ['"', '"'],
        copySinkTypes: [],
        copySourceTypes: [adfModelTypeMap.postgreSql.copySourceType],
        category: ['Database'],
        controls: [
            {
                type: 'select-button-switch',
                required: true,
                isLabelVertical: true,
                options: [
                    {
                        label: stringResources.ModelName_ConnectionString,
                        value: false
                    },
                    {
                        label: stringResources.AzureKeyVault_DisplayText,
                        value: true,
                        hideAKVOptionIfNotChosen: false
                    }
                ],
                bind: 'enableAKV',
                isNotRelatedToCredential: true,
                defaultValue: false,
                cases: [
                    {
                        caseValue: true,
                        children: [
                            {
                                enableAKVParameterization: true,
                                type: 'azure-key-vault',
                                bind: 'referedAKV',
                                isLabelVertical: true,
                                description: format(
                                    stringResources.AzureKeyVaultSecretName_Tooltip_Template_ConnectionStringWithSample,
                                    format(
                                        stringResources.ModelName_LinkedServiceTemplate,
                                        stringResources.PostgreSql_DisplayText
                                    ),
                                    'Server=<server>;Port=<port>;Database=<database>;UID=<username>;Password=<password>'
                                )
                            }
                        ]
                    },
                    {
                        caseValue: false,
                        children: [
                            {
                                type: 'parameterizable',
                                child: {
                                    type: 'textbox',
                                    bind: 'server',
                                    label: stringResources.ModelName_ServerName,
                                    required: true,
                                    validators: [{ type: 'required', errorMessage: '' }],
                                    isLabelVertical: true
                                }
                            },
                            {
                                type: 'parameterizable',
                                child: {
                                    type: 'textbox',
                                    bind: 'port',
                                    label: stringResources.ModelName_Port,
                                    isLabelVertical: true,
                                    inputType: 'number',
                                    min: 0,
                                    defaultValue: 5432
                                }
                            },
                            {
                                type: 'parameterizable',
                                child: {
                                    type: 'textbox',
                                    bind: 'database',
                                    label: stringResources.ModelName_DatabaseName,
                                    required: true,
                                    validators: [{ type: 'required', errorMessage: '' }],
                                    isLabelVertical: true
                                }
                            },
                            {
                                type: 'parameterizable',
                                child: {
                                    type: 'textbox',
                                    bind: 'userName',
                                    label: stringResources.ModelName_UserName,
                                    required: true,
                                    validators: [{ type: 'required', errorMessage: '' }],
                                    isLabelVertical: true
                                }
                            },
                            {
                                type: 'dynamic-akv-switcher',
                                hideAKVSwitcherInConnStrProps: true,
                                enableAKVParameterization: true,
                                bind: 'password',
                                label: stringResources.ModelName_Password,
                                child: {
                                    type: 'parameterizable',
                                    attribute: 'password',
                                    disableAddParameter: true,
                                    child: {
                                        type: 'password',
                                        label: stringResources.ModelName_Password,
                                        required: true,
                                        isLabelVertical: true,
                                        validators: [{ type: 'required', errorMessage: '' }]
                                    }
                                },
                                description: format(
                                    stringResources.AzureKeyVaultSecretName_Tooltip_Template,
                                    format(
                                        stringResources.ModelName_LinkedServiceTemplate,
                                        stringResources.PostgreSql_DisplayText
                                    ),
                                    stringResources.ModelName_Password
                                )
                            },
                            {
                                type: 'select-switch',
                                bind: 'encryptionMethod',
                                label: stringResources.Postgresql_Encryption_Method,
                                isLabelVertical: true,
                                options: encryptionMethodOptions,
                                defaultValue: EncryptionMethod.NoEncryption,
                                cases: [
                                    {
                                        caseValue: EncryptionMethod.NoEncryption,
                                        children: []
                                    },
                                    {
                                        caseValue: null,
                                        children: [
                                            {
                                                type: 'radio-switch',
                                                bind: 'validateServerCertificate',
                                                isLabelVertical: true,
                                                options: [
                                                    {
                                                        label: stringResources.Postgresql_Validate_Server_Certificate,
                                                        value: true
                                                    },
                                                    {
                                                        label: stringResources.Postgresql_Not_Validate_Server_Certificate,
                                                        value: false
                                                    }
                                                ],
                                                defaultValue: true
                                            }
                                        ]
                                    }
                                ]
                            },
                            {
                                type: 'key-val-list',
                                bind: 'additionalFields',
                                label: stringResources.AdditionalConnectionStringFields
                            }
                        ]
                    }
                ]
            }
        ]
    };
    return result;
};
